import { api } from '@/lib/api/client';

/**
 * Web Push 구독 유틸. (2026-08-22)
 *
 * 브라우저 구독 생성·해제와 서버 등록을 한 곳에 모은다.
 * 화면(PushSettings)은 이것만 부른다.
 *
 * 🔴 아이폰은 홈 화면에 추가된 PWA 에서만 동작한다 (iOS 16.4+)
 *   사파리 탭에서는 pushManager 자체가 없어 supported() 가 false 다.
 *   화면이 그 경우 "홈 화면에 추가 후 사용" 안내를 띄운다.
 */

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** 서버의 VAPID 공개키. 키 발급 전이면 null - 화면이 기능을 숨긴다. */
export async function fetchPublicKey(): Promise<string | null> {
  return api.get<string | null>('/push/public-key');
}

/** 이 기기의 현재 브라우저 구독. 없으면 null. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * "이 기기에서 알림을 받겠다" 는 사용자의 의사. (2026-09-04 사용자 신고)
 *
 * 🔴 왜 이것이 필요한가 — 배포할 때마다 알림이 꺼지고 있었다
 *
 *   강제 갱신(forceRefresh)이 서비스 워커를 해제하는데, 푸시 구독은 워커 등록에
 *   붙어 있어 함께 사라진다. 그것을 되살리는 장치가 sessionStorage 의 일회용
 *   표시 하나였고, 그 표시는 네 갈래로 끊어졌다.
 *
 *     ① 앱을 닫았다 다시 열면 sessionStorage 가 비어 있다 (폰 PWA 에서 흔하다)
 *     ② 표시를 시도 "전에" 지워서, 한 번 실패하면 영영 안 켜진다
 *     ③ 서비스 워커 등록 콜백 안에서만 불려서, 그것이 안 뜨면 복구도 없다
 *     ④ 🔴 해제 시점에 구독을 못 읽으면(ready 지연) 표시 자체를 안 남기는데,
 *        워커 해제는 그것과 무관하게 구독을 죽인다
 *
 *   그래서 "복구해야 한다" 는 한 번짜리 신호 대신 **"켜두고 싶다" 는 지속되는
 *   의사**를 남긴다. localStorage 라 강제 갱신·앱 재시작·기기 재부팅을 넘긴다
 *   (forceRefresh 가 지우는 것은 파일 캐시뿐이다).
 *
 * 🔴 권한(Notification.permission)으로 대신할 수 없다
 *   사용자가 우리 화면에서 알림을 껐어도 브라우저 권한은 granted 로 남는다.
 *   권한만 보고 되살리면 끈 것이 계속 되살아난다.
 *
 * 🔴 회원마다 따로 기록한다 (2026-09-04 사용자 질문)
 *   localStorage 는 브라우저(출처) 단위라 그대로 두면 계정을 안 가린다.
 *   가족이 PC 하나를 나눠 쓰는 구조라 실제로 사고가 난다 -
 *   A 가 켜둔 브라우저에 B 가 로그인하면, B 는 켠 적이 없는데 자가 복구가
 *   구독을 만들고 **그 기기의 알림이 통째로 B 에게 넘어간다.**
 *   (같은 endpoint 로 다시 구독하면 마지막 회원이 이긴다 - PushSubscription 주석)
 *
 *   그래서 키에 회원 id 를 붙인다. 로그아웃해도 각자의 표시가 남아,
 *   다시 로그인하면 그 사람의 설정이 그대로 살아난다.
 */
const PUSH_WANTED_PREFIX = 'bada:push:wanted:';

function wantedKey(memberId: number): string {
  return `${PUSH_WANTED_PREFIX}${memberId}`;
}

function wantsPush(memberId: number): boolean {
  try {
    return window.localStorage.getItem(wantedKey(memberId)) === '1';
  } catch {
    // 사파리 프라이빗 모드 등. 자가 복구를 포기할 뿐 나머지는 그대로 동작한다
    return false;
  }
}

function setWantsPush(memberId: number, wanted: boolean): void {
  try {
    if (wanted) {
      window.localStorage.setItem(wantedKey(memberId), '1');
    } else {
      window.localStorage.removeItem(wantedKey(memberId));
    }
  } catch {
    // 못 남겨도 이번 켜기·끄기 자체는 성공한다. 다음 배포에서 복구가 안 될 뿐이다
  }
}

/**
 * 지금 로그인한 회원 id. 못 읽으면 null.
 *
 * 자가 복구가 "누구의 설정인가" 를 가르는 데 쓴다.
 * 로그인 전이거나 세션이 끊겼으면 null 이고, 그때는 복구하지 않는다 -
 * 누구 것인지 모르는 채로 구독을 만들면 엉뚱한 계정에 붙는다.
 */
async function currentMemberId(): Promise<number | null> {
  try {
    const me = await api.get<{ id: number }>('/auth/me');
    return typeof me?.id === 'number' ? me.id : null;
  } catch {
    return null;
  }
}

/**
 * 알림을 켠다: 권한 요청 → 브라우저 구독 생성 → 서버 등록.
 *
 * @throws Error 사용자가 권한을 거부하면. 화면이 메시지로 보여준다
 */
export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('알림 권한이 거부됐습니다. 브라우저 설정에서 허용으로 바꿔야 합니다.');
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // 알림 없는 푸시(백그라운드 데이터)는 안 쓴다는 선언. 필수값이다
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const json = subscription.toJSON();
  await api.post<boolean>('/push/subscriptions', {
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  });

  /*
    🔴 서버 등록까지 끝난 뒤에 남긴다. 중간에 실패한 것을 "켰다" 로 기록하면
      다음 시작 때 있지도 않은 구독을 되살리려 한다.

    🔴 회원 id 로 기록한다. 이 브라우저를 나눠 쓰는 다른 계정과 섞이면 안 된다
      못 읽으면(세션 만료 등) 기록만 건너뛴다 - 구독 자체는 이미 성공했다
  */
  const memberId = await currentMemberId();
  if (memberId !== null) {
    setWantsPush(memberId, true);
  }
}

/** 알림을 끈다: 서버 등록 삭제 → 브라우저 구독 해제. 순서가 반대면 endpoint 를 잃는다. */
export async function disablePush(): Promise<void> {
  // 🔴 의사를 먼저 지운다. 아래에서 실패해도 자가 복구가 되살리지 않게
  const memberId = await currentMemberId();
  if (memberId !== null) {
    setWantsPush(memberId, false);
  }

  const subscription = await currentSubscription();
  if (!subscription) {
    return;
  }
  await api.delete<boolean>(
    `/push/subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`,
  );
  await subscription.unsubscribe();
}

/**
 * 켜뒀던 알림이 끊겼으면 조용히 되살린다. (2026-09-04)
 *
 * 앱이 뜰 때마다 부른다. 아무것도 할 일이 없으면 즉시 끝난다(멱등).
 *
 * 두 가지 끊김을 고친다
 *   구독이 통째로 사라진 경우 - 강제 갱신이 워커를 해제한 뒤
 *   서버만 모르는 경우       - 구독은 살아 있는데 서버 행이 지워졌을 때
 *
 * 🔴 권한 팝업이 뜨지 않는다
 *   permission 이 이미 granted 일 때만 진행한다. requestPermission 은
 *   granted 상태에서 즉시 resolve 되므로 사용자에게 아무것도 안 보인다.
 *
 * 🔴 조용히 실패한다
 *   앱 시작 경로라, 여기서 던지면 화면이 안 뜬다. 복구가 안 되면
 *   설정 화면의 "알림 켜기" 가 여전히 남아 있다.
 */
export async function ensurePushSubscribed(): Promise<void> {
  if (!pushSupported()) {
    return;
  }
  try {
    /*
      🔴 누구의 설정인지 먼저 가른다 (2026-09-04)
        로그인 전이거나 세션이 끊겼으면 아무것도 하지 않는다.
        누구 것인지 모르는 채로 구독을 만들면 엉뚱한 계정에 붙는다.
    */
    const memberId = await currentMemberId();
    if (memberId === null || !wantsPush(memberId)) {
      return;
    }

    if (pushRestoreAction(true, Notification.permission, null) === 'skip') {
      // 브라우저·OS 설정에서 막힌 것이다. 우리가 되살릴 수 없다
      return;
    }

    const subscription = await currentSubscription();
    const known =
      subscription === null
        ? false
        : await api.get<boolean>('/push/subscribed', { endpoint: subscription.endpoint });

    if (pushRestoreAction(true, Notification.permission, known) === 'skip') {
      return;
    }

    const publicKey = await fetchPublicKey();
    if (publicKey === null) {
      return;
    }
    await enablePush(publicKey);
  } catch {
    // 조용히 둔다. 다음 시작에서 다시 시도한다 - 일회용 표시가 아니라
    // 지속되는 의사라 기회가 계속 온다
  }
}

/**
 * 자가 복구를 해야 하는가. (2026-09-04)
 *
 * 판단을 순수 함수로 떼어낸 이유 - 이 규칙이 틀리면 **조용히** 틀린다.
 * 알림이 안 오는 것으로만 드러나고, 그것을 알아채려면 며칠이 걸린다.
 * 브라우저 API 없이 네 갈래를 전부 테스트로 박아 둔다.
 *
 * @param wanted     사용자가 이 기기에서 받겠다고 한 적이 있나 (localStorage)
 * @param permission 브라우저 알림 권한
 * @param serverKnows 서버가 지금 구독을 아는가. 아직 안 물어봤으면 null
 */
export function pushRestoreAction(
  wanted: boolean,
  permission: NotificationPermission,
  serverKnows: boolean | null,
): 'skip' | 'subscribe' {
  // 🔴 권한만 보고 되살리면 사용자가 끈 것이 계속 살아난다.
  //   우리 화면에서 꺼도 브라우저 권한은 granted 로 남기 때문이다
  if (!wanted) {
    return 'skip';
  }
  // 브라우저·OS 에서 막혔다. 되살릴 방법이 없고 팝업만 반복된다
  if (permission !== 'granted') {
    return 'skip';
  }
  // 서버까지 아는 정상 상태. 할 일이 없다
  if (serverKnows === true) {
    return 'skip';
  }
  return 'subscribe';
}

/** 이 기기가 내 구독으로 서버에 등록돼 있는가. */
export async function isSubscribed(): Promise<boolean> {
  const subscription = await currentSubscription();
  if (!subscription) {
    return false;
  }
  return api.get<boolean>('/push/subscribed', { endpoint: subscription.endpoint });
}

/**
 * 테스트 발송 결과. (2026-09-03 확장)
 *
 * 🔴 성공 수만으로는 확인이 안 된다
 *   "2대로 보냈습니다" 라고만 하면 내 PC 가 그 둘에 들었는지 알 수 없다.
 *   켜둔 기기 수와 함께 줘야 화면이 "3대 중 2대" 라고 말할 수 있다.
 */
export interface PushTestResult {
  /** 켜둔 기기 수 */
  devices: number;
  /** 실제로 도착한 기기 수. devices 와 다르면 실패한 기기가 있다 */
  sent: number;
  /** Slack 으로도 보냈는가. 끈 사람은 false */
  slack: boolean;
}

/**
 * 켜둔 기기 전부와 Slack 으로 테스트 알림을 보낸다.
 *
 * 🔴 실제 가격 알림과 같은 경로를 탄다 (2026-09-03)
 *   전에는 푸시만 보내서 "Slack 은 왜 안 오지" 가 됐다.
 *   테스트가 실제와 다른 경로를 타면 확인의 뜻이 없다.
 */
export async function sendTestPush(): Promise<PushTestResult> {
  return api.post<PushTestResult>('/push/test');
}

/**
 * 알림 받는 곳 설정. (2026-09-03 사용자 요청, V57)
 *
 * 기기(폰·PC)는 구독 행이 이미 나뉘어 있어 행마다 스위치가 있고,
 * Slack 은 회원 설정 하나다.
 */
export interface NotificationDevice {
  id: number;
  /** "아이폰 (사파리)" 같은 이름. 서버가 User-Agent 로 추정한다. 못 알아봤으면 null */
  label: string | null;
  /** 이 기기로 보내는가. 끄면 구독은 남고 발송만 건너뛴다 */
  enabled: boolean;
  /** 마지막으로 알림이 간 시각. 한 번도 없으면 null */
  lastSentAt: string | null;
  /** 지금 이 브라우저인가 */
  current: boolean;
}

export interface NotificationSettings {
  devices: NotificationDevice[];
  slackEnabled: boolean;
  /** Slack 개인 알림을 켤 수 있는가 = 관리자인가 (2026-09-28). 아니면 화면이 스위치 대신 "관리자만" 을 보인다 */
  slackAllowed: boolean;
}

/**
 * 내 알림 설정을 받아온다.
 *
 * 지금 브라우저의 endpoint 를 함께 보내 목록에서 "지금 이 기기" 를 표시한다.
 * 알림을 안 켠 브라우저에서는 endpoint 가 없지만, 다른 기기 목록은 봐야 하므로
 * 없이도 부른다.
 */
export async function fetchNotificationSettings(): Promise<NotificationSettings> {
  const subscription = pushSupported() ? await currentSubscription() : null;
  return api.get<NotificationSettings>(
    '/push/settings',
    subscription ? { endpoint: subscription.endpoint } : undefined,
  );
}

/** 기기를 켜고 끄거나 이름을 바꾼다. 준 것만 바뀐다 */
export async function updateNotificationDevice(
  deviceId: number,
  patch: { enabled?: boolean; label?: string },
): Promise<void> {
  await api.put<boolean>(`/push/devices/${deviceId}`, patch);
}

/**
 * 기기를 목록에서 지운다.
 *
 * 🔴 끄기와 다르다. 다시 받으려면 그 브라우저에서 알림을 새로 켜야 한다.
 *   잃어버린 폰이나 반납한 PC 를 정리하는 용도다.
 */
export async function removeNotificationDevice(deviceId: number): Promise<void> {
  await api.delete<boolean>(`/push/devices/${deviceId}`);
}

/** Slack 개인 알림을 켜고 끈다 */
export async function setSlackNotification(enabled: boolean): Promise<void> {
  await api.put<boolean>('/push/slack', { enabled });
}

/**
 * VAPID 공개키(base64url)를 subscribe 가 요구하는 바이트 배열로 바꾼다.
 * 표준 변환이다 - 브라우저 API 가 문자열을 안 받는 것뿐이다.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  // TS 5.9 의 Uint8Array 는 SharedArrayBuffer 일 수도 있는 제네릭이 됐다.
  // subscribe 가 요구하는 것은 일반 ArrayBuffer 라 명시해서 만든다
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}
